import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import { useCareCoordinator } from '../care/CareCoordinatorContext';
import { getApiBase } from '../utils/runtime';
import './care/CarePanels.css';

const API_BASE = getApiBase();

function AddNote() {
  const { id: patientId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addObservation } = useCareCoordinator();

  const [form, setForm] = useState({
    content: '',
    noteType: 'provider_observation',
    category: 'symptom',
    issue: '',
    symptom: '',
    severity: 'medium',
    recommendedFollowUp: '',
    expectedReviewDate: ''
  });
  const [appointments, setAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/patients/${patientId}/appointments`)
      .then((res) => res.json())
      .then((data) => setAppointments(Array.isArray(data) ? data : []))
      .catch(() => setAppointments([]));
  }, [patientId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const authorId = user?.user_id || user?.sub;

    if (!form.content.trim() || !authorId) {
      setError('The note content and your identifier are required.');
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/patients/${patientId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: form.content,
          appointment_id: selectedAppointment || null,
          author_id: authorId
        })
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Unable to save the note.');
      }

      addObservation({
        subjectId: patientId,
        appointmentId: selectedAppointment || null,
        noteType: form.noteType,
        category: form.category,
        issue: form.issue,
        symptom: form.symptom,
        severity: form.severity,
        freeText: form.content,
        recommendedFollowUp: form.recommendedFollowUp,
        expectedReviewDate: form.expectedReviewDate
      });

      navigate(`/professional/clients/${patientId}`);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '2rem 1rem 4rem' }}>
      <form className="care-form" onSubmit={handleSubmit}>
        <div className="care-panel__header">
          <div>
            <h3>Structured observation and smart note</h3>
            <p>Save the legacy note to the existing record while also capturing structured fields for future care suggestions and auditability.</p>
          </div>
        </div>

        <div className="care-form__grid">
          <label>
            <span>Note type</span>
            <select value={form.noteType} onChange={(event) => setForm((current) => ({ ...current, noteType: event.target.value }))}>
              <option value="provider_observation">Provider observation</option>
              <option value="symptom_log">Symptom log</option>
              <option value="milestone">Milestone checkpoint</option>
            </select>
          </label>
          <label>
            <span>Category</span>
            <select value={form.category} onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}>
              <option value="symptom">Symptom</option>
              <option value="mobility">Mobility</option>
              <option value="behavior">Behavior</option>
              <option value="wound">Wound / skin</option>
              <option value="follow_up">Follow-up</option>
            </select>
          </label>
          <label>
            <span>Issue</span>
            <input value={form.issue} onChange={(event) => setForm((current) => ({ ...current, issue: event.target.value }))} />
          </label>
          <label>
            <span>Symptom keyword</span>
            <input value={form.symptom} onChange={(event) => setForm((current) => ({ ...current, symptom: event.target.value }))} />
          </label>
          <label>
            <span>Severity</span>
            <select value={form.severity} onChange={(event) => setForm((current) => ({ ...current, severity: event.target.value }))}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
          <label>
            <span>Expected review date</span>
            <input type="date" value={form.expectedReviewDate} onChange={(event) => setForm((current) => ({ ...current, expectedReviewDate: event.target.value }))} />
          </label>
          <label>
            <span>Recommended follow-up</span>
            <input value={form.recommendedFollowUp} onChange={(event) => setForm((current) => ({ ...current, recommendedFollowUp: event.target.value }))} />
          </label>
          <label>
            <span>Linked appointment</span>
            <select value={selectedAppointment} onChange={(event) => setSelectedAppointment(event.target.value)}>
              <option value="">None</option>
              {appointments.map((appointment) => (
                <option key={appointment.appointment_id} value={appointment.appointment_id}>
                  {`${new Date(appointment.start_utc || appointment.start_time).toLocaleDateString()} - ${appointment.service_name}`}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label>
          <span>Smart note</span>
          <textarea
            rows={6}
            value={form.content}
            onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))}
            placeholder="Describe the observation, expected outcome, concern, or progress update."
          />
        </label>

        {error ? <p style={{ color: '#b91c1c' }}>{error}</p> : null}

        <div className="care-form__actions">
          <button className="care-form__primary" type="submit">Save note and observation</button>
          <button className="care-form__secondary" type="button" onClick={() => navigate(`/professional/clients/${patientId}`)}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddNote;
